import { UploadPhotoTemplate } from '@templates';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@bikes/routes/Routes';
import { useEffect, useState } from 'react';
import { ErrorMessages } from '@types';
import { useBikesStore } from '@bikes/store/store';
import * as Sentry from '@sentry/react';
import { FileObject, useUploadImage, useDeleteImage } from '@hooks';
import {
  convertFilePartsToFileObjects,
  handleFileChange,
  handleFileDelete,
  handleUploadFile,
} from '@utils';
import { UploadPhotoTextContent } from './UploadPhotoTextContent';

export const UploadPhotoPage = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState<FileObject[]>([]);
  const {
    bikesType,
    bikesTypeLabel,
    uploadedImages,
    uploadedImagesFileName,
    uploadedImagesFileType,
    uploadedImagesBlob,
    updateUploadedImages,
    updateIsPhotoLocationStored,
    fileWithLoadedCoordinates,
    updateFileWithLoadedCoordinates,
    updateServiceLocationDetails,
    updateUploadedImagesBlobs,
  } = useBikesStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const { mutateAsync: mutateImageUpload, isPending } = useUploadImage();
  const { mutate: mutateDelete } = useDeleteImage();
  const [processingPhoto, setProcessingPhoto] = useState(false);

  const errorPath = 'upload-photo';
  const pageTitle = `Upload a photo of the ${bikesTypeLabel}`;

  useEffect(() => {
    convertFilePartsToFileObjects({
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
    }).then((images) => {
      setUploadedFiles(images);
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFileChange = async (files: File[]) => {
    await handleFileChange({
      files,
      setProcessingPhoto,
      uploadedFiles,
      setUploadedFiles,
      setErrorMessages,
      updateUploadedImages,
      updateIsPhotoLocationStored,
      updateFileWithLoadedCoordinates,
      updateServiceLocationDetails,
      errorPath,
    });
  };

  const handleUploadError = (error: string) => {
    if (error) {
      Sentry.captureException(error);
      setErrorMessages([{ path: 'upload-photo', message: error }]);
    }
  };

  const handleDelete = (files: File[]) => {
    handleFileDelete({
      files,
      setProcessingPhoto,
      uploadedFiles,
      setUploadedFiles,
      setErrorMessages,
      updateUploadedImages,
      updateIsPhotoLocationStored,
      fileWithLoadedCoordinates,
      updateFileWithLoadedCoordinates,
      updateServiceLocationDetails,
      mutateDelete,
      errorPath,
    });
  };

  const handleNextClick = async () => {
    await handleUploadFile({
      uploadedFiles,
      uploadedImagesBlob,
      navigate,
      navigatePath: RoutesList.BIKES_LOCATION,
      mutateImageUpload,
      errorPath,
      setErrorMessages,
      updateUploadedImagesBlobs,
    });
  };

  const handlePreviousClick = () => {
    navigate(RoutesList.BIKES_HOME);
  };

  const buttonNext = {
    label: 'I do not have a photo',
    loading: isPending,
    disabled: processingPhoto,
    onClick: handleNextClick,
  };

  const buttonPrevious = {
    disabled: processingPhoto || isPending,
    onClick: handlePreviousClick,
  };

  const pageTextContent = (
    <UploadPhotoTextContent
      bikesType={bikesType}
      bikesTypeLabel={bikesTypeLabel}
    />
  );

  return (
    <UploadPhotoTemplate
      formTitle={pageTitle}
      description={pageTextContent}
      handleFileChange={onFileChange}
      handleUploadError={handleUploadError}
      handleDelete={handleDelete}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      maxNumberOfUpload={5}
      errorPath={errorPath}
      errorMessages={errorMessages}
      initialFiles={uploadedFiles.map((file) => file.file)}
    />
  );
};
